import React, { useState, useMemo, useCallback } from 'react';
import QRCode from 'qrcode';
import { toast } from 'react-toastify';

const defaultState = {
  packageId: 'com.readwell.adult',
  source: 'flyer_general',
  medium: 'qr',
  campaign: 'pilot_v1',
  term: '',
  content: ''
};

const fieldStyle = { display:'flex', flexDirection:'column', gap:4 };
const labelStyle = { fontSize:12, fontWeight:600, textTransform:'uppercase', letterSpacing:'.5px' };

const QrGenerator = () => {
  const [form, setForm] = useState(defaultState);
  const [previewDataUrl, setPreviewDataUrl] = useState('');
  const [generating, setGenerating] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value.trim() }));
  };

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set('utm_source', form.source || 'unknown');
    params.set('utm_medium', form.medium || 'qr');
    params.set('utm_campaign', form.campaign || 'default');
    if (form.term) params.set('utm_term', form.term);
    if (form.content) params.set('utm_content', form.content);
    return params.toString();
  }, [form]);

  const playUrl = useMemo(() => {
    if (!form.packageId) return '';
    return `https://play.google.com/store/apps/details?id=${form.packageId}&referrer=${encodeURIComponent(queryString)}`;
  }, [form.packageId, queryString]);

  const generatePreview = useCallback(async () => {
    if (!playUrl) { setPreviewDataUrl(''); return; }
    try {
      setGenerating(true);
      const dataUrl = await QRCode.toDataURL(playUrl, { width: 300, margin: 2, errorCorrectionLevel: 'H' });
      setPreviewDataUrl(dataUrl);
    } catch (e) {
      toast.error('Failed generating QR');
    } finally {
      setGenerating(false);
    }
  }, [playUrl]);

  // Auto preview on changes (debounced by requestAnimationFrame minimal approach)
  React.useEffect(() => { generatePreview(); }, [generatePreview]);

  const downloadPng = async () => {
    try {
      const dataUrl = await QRCode.toDataURL(playUrl, { width: 1024, margin: 2, errorCorrectionLevel: 'H' });
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `qr_${form.source || 'src'}_${form.campaign || 'cmp'}.png`;
      a.click();
    } catch (e) { toast.error('PNG generation failed'); }
  };

  const downloadSvg = async () => {
    try {
      const svg = await QRCode.toString(playUrl, { type:'svg', margin:2, errorCorrectionLevel:'H' });
      const blob = new Blob([svg], { type:'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qr_${form.source || 'src'}_${form.campaign || 'cmp'}.svg`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('SVG generation failed'); }
  };

  const copyUrl = async () => {
    try { await navigator.clipboard.writeText(playUrl); toast.success('Copied URL'); } catch { toast.error('Copy failed'); }
  };

  const reset = () => { setForm(defaultState); };

  return (
    <div style={{padding:'1rem 1.25rem', maxWidth:800}}>
      <h2 style={{marginTop:0}}>Generate Marketing QR Codes</h2>
      <p style={{fontSize:'0.85rem',color:'#555',marginTop:-8}}>Configure UTM parameters to track installs from printed or digital assets.</p>
      <div style={{display:'grid',gap:'1rem',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',marginTop:'1.25rem'}}>
        <div style={fieldStyle}>
          <label style={labelStyle} htmlFor='packageId'>Android Package ID</label>
          <input id='packageId' name='packageId' value={form.packageId} onChange={onChange} placeholder='com.example.app'/>
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle} htmlFor='source'>utm_source *</label>
          <input id='source' name='source' value={form.source} onChange={onChange} placeholder='flyer_general'/>
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle} htmlFor='medium'>utm_medium *</label>
          <input id='medium' name='medium' value={form.medium} onChange={onChange} placeholder='qr'/>
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle} htmlFor='campaign'>utm_campaign *</label>
          <input id='campaign' name='campaign' value={form.campaign} onChange={onChange} placeholder='pilot_v1'/>
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle} htmlFor='term'>utm_term</label>
          <input id='term' name='term' value={form.term} onChange={onChange} placeholder='keyword'/>
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle} htmlFor='content'>utm_content</label>
          <input id='content' name='content' value={form.content} onChange={onChange} placeholder='variant_a'/>
        </div>
      </div>
      <div style={{marginTop:'1rem'}}>
        <code style={{fontSize:12,wordBreak:'break-all',display:'block',background:'#f5f5f5',padding:'8px',borderRadius:4}}>{playUrl}</code>
      </div>
      <div style={{display:'flex',flexWrap:'wrap',gap:'0.5rem',marginTop:'0.75rem'}}>
        <button onClick={copyUrl} disabled={!playUrl}>Copy URL</button>
        <button onClick={downloadPng} disabled={!playUrl || generating}>Download PNG</button>
        <button onClick={downloadSvg} disabled={!playUrl || generating}>Download SVG</button>
        <button onClick={reset}>Reset</button>
      </div>
      <div style={{marginTop:'1.5rem',display:'flex',gap:'2rem',flexWrap:'wrap',alignItems:'center'}}>
        {previewDataUrl && (
          <img src={previewDataUrl} width={300} height={300} alt='QR preview' style={{border:'1px solid #eee',background:'#fff'}} />
        )}
        <ul style={{fontSize:'0.75rem',lineHeight:1.5,color:'#666',paddingLeft:'1rem'}}>
          <li>Preview is 300px; downloads generate a high-resolution version.</li>
          <li>Ensure contrast when printing; avoid placing QR on dark backgrounds.</li>
          <li>Scan test with multiple devices before distribution.</li>
        </ul>
      </div>
    </div>
  );
};

export default QrGenerator;
